import React, { useMemo, useState } from "react";

import {
  faCalculatorSimple,
  faCode,
  faMinus,
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { HighlightedText } from "src/base-components/HighlightedText";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { useEntitySchemas } from "src/entities/queries";
import {
  EntityFilter,
  EventTypeFilter,
} from "src/featureCatalogue/Features/FilterActions";
import { StatusToggleRequest } from "src/featureCatalogue/Features/StatusDropdown";
import { useFeatureQueries } from "src/featureCatalogue/queries";
import { TableActions } from "src/featureCatalogue/TableActions";
import { useFilteredData } from "src/featureCatalogue/useFilteredData";
import {
  commonColumns,
  EntityPills,
  FilterOptions,
  formatAthenaSqlQuery,
  Query,
  statusColumns,
} from "src/featureCatalogue/utils";
import { WhitePane } from "src/jobs/common/WhitePane";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  DashboardPageParams,
  getUrlToManageFeatureQueryPage,
} from "src/router/urls";
import { getTypeIcon } from "src/utils/icons";
import { useParamsDecode } from "src/utils/useParamsDecode";

const columnHelper = createColumnHelper<Query>();

const getQueryViewColumns = (
  filterOptions: FilterOptions,
  onRequestStatusChange: (request: StatusToggleRequest) => void,
  entityDisplayNameById: Record<string, string>,
) => [
  columnHelper.accessor((row) => row.query_name || "Untitled Feature Query", {
    id: "query_name",
    header: (info) => <Header info={info}>Query Name</Header>,
    cell: (info) => <DisplaySqlCell info={info} />,
    minSize: 250,
    maxSize: 250,
    filterFn: "includesString",
    enableGlobalFilter: true,
    meta: { sticky: "left" },
  }),
  columnHelper.accessor(
    (row) =>
      row.columns.features?.map((feature) => feature.feature_key).join(" ") ??
      "",
    {
      id: "feature_key",
      header: (info) => <Header info={info}>Feature Key</Header>,
      cell: (info) => {
        const features = info.row.original.columns.features ?? [];

        return (
          <Cell className="h-auto" info={info}>
            <div className="flex min-h-fit flex-col items-start space-y-2">
              {features?.map((feature) => (
                <Tooltip
                  key={feature.feature_key}
                  delay={250}
                  headerAction={
                    <CopyTextIcon
                      variant="inverted"
                      feedback="inline-only-checkmark"
                      value={feature.feature_key}
                    />
                  }
                  placement="top"
                  title={feature.feature_key}
                  asChild
                >
                  <Pill size="sm" variant="gray">
                    <Pill.Icon
                      icon={getTypeIcon(feature.feature_type ?? "", faMinus)}
                    />
                    <Pill.Text>
                      <HighlightedText
                        highlight={info.table.getState().globalFilter}
                        highlightClassName="font-inter-semibold-12px bg-yellow-200"
                        text={feature.feature_key}
                      />
                    </Pill.Text>
                  </Pill>
                </Tooltip>
              ))}
            </div>
          </Cell>
        );
      },
      minSize: 280,
      maxSize: 280,
      filterFn: "includesString",
      enableGlobalFilter: true,
    },
  ),
  columnHelper.accessor(
    (row) =>
      row.columns.entities?.map((entity) => entity.entity_name).join(" ") ?? "",
    {
      id: "entities",
      header: (info) => (
        <Header info={info}>
          <div className="flex w-full justify-between gap-x-2">
            <span>Entity</span>
            <div className="flex gap-x-1">
              <EntityFilter
                options={filterOptions.entityTypes}
                property="entities"
              />
            </div>
          </div>
        </Header>
      ),
      minSize: 150,
      maxSize: 150,
      cell: (info) => {
        const entities = info.row.original.columns.entities;
        return (
          <Cell info={info}>
            <EntityPills
              displayNameById={entityDisplayNameById}
              entities={entities?.map((e) => e.entity_name) ?? []}
            />
          </Cell>
        );
      },
      filterFn: "includesString",
      enableSorting: true,
      enableGlobalFilter: true,
    },
  ),
  // Event column
  columnHelper.accessor("event", {
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-2">
          <span>Event</span>
          <EventTypeFilter options={filterOptions.eventTypes} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Icon icon={faCalculatorSimple} />
          <Pill.Text>{info.getValue()}</Pill.Text>
        </Pill>
      </Cell>
    ),
    minSize: 150,
    maxSize: 150,
    enableSorting: true,
  }),
  ...commonColumns<Query>().filter((column) => column.accessorKey !== "event"),
  ...statusColumns(columnHelper, {
    getEnvironmentSettings: (row) => row.environment_settings,
    hasQuery: (row) => !!row.id,
    getQueryId: (row) => row.id,
    getEtag: (row) => row.etag,
    getEntities: (row) => row.columns.entities?.map((e) => e.entity_name) ?? [],
    getEvent: (row) => row.event,
    getFeatures: (row) => row.columns.features ?? [],
    onRequestStatusChange,
  }),
  columnHelper.display({
    id: "actions",
    header: (info) => (
      <Header info={info}>
        <span />
      </Header>
    ),
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <TableActions query={info.row.original} />
      </Cell>
    ),
    meta: { sticky: "right" },
  }),
];

export const QUERY_FILTER_CONFIG = {
  entityFilterKey: "filter[entities][eq]",
  getEntityValues: (q: Query) =>
    q.columns.entities?.map((e) => e.entity_name) ?? [],
  getEvent: (q: Query) => q.event ?? "",
  getSandboxStatus: (q: Query) => q.environment_settings?.sandbox?.status,
  getLiveStatus: (q: Query) => q.environment_settings?.live?.status,
};

export const Queries: React.FC<{
  searchQuery?: string;
  resetSearch?: () => void;
  onRequestStatusChange: (request: StatusToggleRequest) => void;
}> = ({ searchQuery, resetSearch, onRequestStatusChange }) => {
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });
  const entityDisplayNameById = useMemo(
    () =>
      Object.fromEntries(
        entitySchemasData?.entities.map((schema) => [
          schema._id,
          schema._display_name_singular,
        ]) ?? [],
      ),
    [entitySchemasData],
  );
  const { data, isLoading, isSuccess } = useFeatureQueries();

  const queries = useMemo(
    () =>
      (data?.filter((item): item is Query => item.id !== undefined) ??
        []) as Query[],
    [data],
  );

  const {
    filteredItems: filteredQueries,
    hasActiveFilters,
    resetFilters,
  } = useFilteredData({
    items: queries,
    filterConfig: QUERY_FILTER_CONFIG,
  });

  const filterOptions = useMemo(() => {
    const entityTypes = [
      ...new Set(
        queries.flatMap(
          (q) => q.columns.entities?.map((e) => e.entity_name) ?? [],
        ),
      ),
    ];
    const eventTypes = [
      ...new Set(queries.map((q) => q.event).filter((e): e is string => !!e)),
    ];
    return { entityTypes, eventTypes };
  }, [queries]);

  const columns = useMemo(
    () =>
      getQueryViewColumns(
        filterOptions,
        onRequestStatusChange,
        entityDisplayNameById,
      ),
    [filterOptions, onRequestStatusChange, entityDisplayNameById],
  );

  const showEmptyState = !isLoading && isSuccess && !filteredQueries.length;
  const isFilteredEmptyState =
    showEmptyState && (hasActiveFilters || !!searchQuery);

  const emptyStateNode = (
    <EmptyState
      actionButton={
        !searchQuery
          ? {
              icon: faPlus,
              label: "Create Feature Query",
              onClick: () => {
                navigate(getUrlToManageFeatureQueryPage(orgId, wsId));
              },
            }
          : undefined
      }
      dataLoc="queries-empty-state"
      description={
        searchQuery
          ? "Try adjusting your search to find what you're looking for"
          : "Create Feature Queries"
      }
      headline={
        searchQuery ? "No matching queries found" : "No Feature Queries found"
      }
      icon={searchQuery ? faSearch : faCode}
    />
  );

  const filterEmptyStateNode = (
    <EmptyState
      actionButton={
        hasActiveFilters || searchQuery
          ? {
              icon: faSearch,
              label: "Clear filters",
              onClick: () => {
                resetFilters();
                resetSearch?.();
              },
            }
          : undefined
      }
      dataLoc="queries-filter-empty-state"
      description="Try adjusting your search or filters to find what you're looking for"
      headline="No matching queries found"
      icon={faSearch}
    />
  );

  return (
    <WhitePane>
      <div className="flex h-full w-full flex-col">
        {isSuccess && queries.length === 0 ? (
          emptyStateNode
        ) : (
          <div
            className={twJoin(
              "relative flex min-h-0 flex-1 flex-col p-4",
              showEmptyState && "items-center justify-center",
            )}
          >
            <TableComp
              columns={columns}
              data={filteredQueries}
              dataLoc="queries-list"
              emptyState={filteredQueries.length > 0 && filterEmptyStateNode}
              frameClassName="w-full min-h-0 flex-1"
              globalFilter={searchQuery}
              initialState={{
                sorting: [
                  { id: "window_length", desc: false },
                  { id: "window_hop", desc: false },
                ],
              }}
              isLoading={isLoading}
              rowPropsGetter={(row) => ({
                onClick: () => {
                  navigate(
                    getUrlToManageFeatureQueryPage(
                      orgId,
                      wsId,
                      row.original.id,
                      "view",
                    ),
                  );
                },
                classNameOverrides:
                  "cursor-pointer hover:bg-gray-50 [&>td.sticky]:z-10 [&>td.sticky]:bg-white [&>td]:align-top [&_.sticky]:hover:bg-gray-50",
                "data-loc": row.original.columns.features?.[0]?.feature_key,
              })}
              variant="compact"
              clientSideFiltering
            />
            {showEmptyState &&
              (isFilteredEmptyState ? filterEmptyStateNode : emptyStateNode)}
          </div>
        )}
      </div>
    </WhitePane>
  );
};

export const DisplaySqlCell: React.FC<{ info: CellContext<Query, string> }> = ({
  info,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryName = info.row.original.query_name || "Untitled Feature Query";
  const sqlQuery = info.row.original.query;

  return (
    <CustomPopover
      button={
        <Cell info={info}>
          <HighlightedText
            highlight={info.table.getState().globalFilter}
            highlightClassName="font-inter-semibold-12px bg-yellow-200"
            text={queryName}
          />
        </Cell>
      }
      className="h-[400px] w-[400px] border-0"
      isOpen={isOpen}
      offsetX={-170}
      offsetY={35}
      placement="right-start"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {isOpen && (
        <div className="flex h-full flex-col rounded-lg px-4 py-2">
          <span className="mb-2 font-inter-semibold-12px">{queryName}</span>
          <CodeEditor
            language="sql"
            value={formatAthenaSqlQuery(sqlQuery)}
            lineWrapping
            readOnly
          />
        </div>
      )}
    </CustomPopover>
  );
};
